import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Alert, Button, Card, Eyebrow, Field, Input, Spinner } from '@busbook/ui';
import type { AuthenticatedUser } from '@busbook/shared';
import { api } from '../lib/api.js';
import { useCustomer } from '../lib/customer-session.js';
import { useT } from '../i18n/index.js';

/**
 * The passenger's own details.
 *
 * Everything here is unverified and matched against nothing. It exists for two reasons: an
 * operator asks for an ID number at boarding, and a booking form should not make somebody
 * retype what they have already told us.
 *
 * Grouped rather than presented as one long column — "you", "where you live", "who to call"
 * are three different questions, and a form that asks all fifteen at once reads as an
 * interrogation. Nothing is required except a name, and nothing blocks booking.
 */

type Draft = {
  name: string;
  phone: string;
  dateOfBirth: string;
  idNumber: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  emergencyName: string;
  emergencyPhone: string;
};

const EMPTY: Draft = {
  name: '',
  phone: '',
  dateOfBirth: '',
  idNumber: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  region: '',
  postalCode: '',
  country: '',
  emergencyName: '',
  emergencyPhone: '',
};

function draftFrom(user: AuthenticatedUser): Draft {
  return {
    name: user.name ?? '',
    phone: user.phone ?? '',
    dateOfBirth: user.dateOfBirth ?? '',
    idNumber: user.idNumber ?? '',
    addressLine1: user.addressLine1 ?? '',
    addressLine2: user.addressLine2 ?? '',
    city: user.city ?? '',
    region: user.region ?? '',
    postalCode: user.postalCode ?? '',
    country: user.country ?? '',
    emergencyName: user.emergencyName ?? '',
    emergencyPhone: user.emergencyPhone ?? '',
  };
}

export function Profile() {
  const t = useT();
  const { user, loading, signOut } = useCustomer();
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    setDraft(draftFrom(user));
    setAvatarUrl(user.avatarUrl);
  }, [user]);

  if (loading) {
    return (
      <div className="grid place-items-center py-20">
        <Spinner label={t('m.loading')} />
      </div>
    );
  }

  if (!user) {
    return (
      <section className="px-4 py-6">
        <Eyebrow>{t('pr.title')}</Eyebrow>
        <h1 className="mt-1 text-xl font-semibold tracking-tight">{t('m.signInToSee')}</h1>
        <a
          href="/auth/login?next=%2Fprofile"
          className="mt-4 inline-block text-sm font-medium underline underline-offset-4"
          style={{ color: 'var(--color-primary)' }}
        >
          {t('c.signIn')}
        </a>
      </section>
    );
  }

  const set = (patch: Partial<Draft>) => {
    setDraft((current) => ({ ...current, ...patch }));
    // A success notice that outlives the value it described is a lie about unsaved work.
    setSaved(false);
  };

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.patch<AuthenticatedUser>('/public/auth/profile', draft);
      setSaved(true);
    } catch {
      setError(t('pr.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="px-4 py-6">
      <Eyebrow>{t('pr.title')}</Eyebrow>
      <h1 className="mt-1 text-xl font-semibold tracking-tight">{draft.name || user.email}</h1>
      <p className="mt-1 text-sm" style={{ color: 'var(--color-muted)' }}>
        {user.email}
      </p>

      <AvatarRow name={draft.name || user.email} url={avatarUrl} onChange={setAvatarUrl} />

      <form onSubmit={submit} className="mt-6 flex flex-col gap-5">
        <Group title={t('pr.you')}>
          <Field label={t('c.yourName')} htmlFor="p-name">
            <Input
              id="p-name"
              autoComplete="name"
              required
              value={draft.name}
              onChange={(e) => set({ name: e.target.value })}
            />
          </Field>
          <Field label={t('pr.phone')} htmlFor="p-phone">
            <Input
              id="p-phone"
              type="tel"
              autoComplete="tel"
              inputMode="tel"
              value={draft.phone}
              onChange={(e) => set({ phone: e.target.value })}
            />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('pr.dob')} htmlFor="p-dob">
              <Input
                id="p-dob"
                type="date"
                autoComplete="bday"
                value={draft.dateOfBirth}
                onChange={(e) => set({ dateOfBirth: e.target.value })}
              />
            </Field>
            <Field label={t('pr.idNumber')} htmlFor="p-id" hint={t('pr.idHint')}>
              <Input
                id="p-id"
                autoComplete="off"
                value={draft.idNumber}
                onChange={(e) => set({ idNumber: e.target.value })}
              />
            </Field>
          </div>
        </Group>

        <Group title={t('pr.address')}>
          <Field label={t('pr.line1')} htmlFor="p-l1">
            <Input
              id="p-l1"
              autoComplete="address-line1"
              value={draft.addressLine1}
              onChange={(e) => set({ addressLine1: e.target.value })}
            />
          </Field>
          <Field label={t('pr.line2')} htmlFor="p-l2">
            <Input
              id="p-l2"
              autoComplete="address-line2"
              value={draft.addressLine2}
              onChange={(e) => set({ addressLine2: e.target.value })}
            />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('pr.city')} htmlFor="p-city">
              <Input
                id="p-city"
                autoComplete="address-level2"
                value={draft.city}
                onChange={(e) => set({ city: e.target.value })}
              />
            </Field>
            <Field label={t('pr.region')} htmlFor="p-region">
              <Input
                id="p-region"
                autoComplete="address-level1"
                value={draft.region}
                onChange={(e) => set({ region: e.target.value })}
              />
            </Field>
            <Field label={t('pr.postalCode')} htmlFor="p-post">
              <Input
                id="p-post"
                autoComplete="postal-code"
                value={draft.postalCode}
                onChange={(e) => set({ postalCode: e.target.value })}
              />
            </Field>
            <Field label={t('pr.country')} htmlFor="p-country" hint={t('pr.countryHint')}>
              <Input
                id="p-country"
                autoComplete="country"
                maxLength={2}
                className="uppercase"
                value={draft.country}
                onChange={(e) => set({ country: e.target.value.toUpperCase() })}
              />
            </Field>
          </div>
        </Group>

        <Group title={t('pr.emergency')} hint={t('pr.emergencyHint')}>
          <Field label={t('pr.emergencyName')} htmlFor="p-ename">
            <Input
              id="p-ename"
              autoComplete="off"
              value={draft.emergencyName}
              onChange={(e) => set({ emergencyName: e.target.value })}
            />
          </Field>
          <Field label={t('pr.emergencyPhone')} htmlFor="p-ephone">
            <Input
              id="p-ephone"
              type="tel"
              inputMode="tel"
              autoComplete="off"
              value={draft.emergencyPhone}
              onChange={(e) => set({ emergencyPhone: e.target.value })}
            />
          </Field>
        </Group>

        {error && <Alert tone="error">{error}</Alert>}
        {saved && <Alert tone="success">{t('pr.saved')}</Alert>}

        <Button type="submit" size="lg" block loading={busy}>
          {t('pr.save')}
        </Button>
      </form>

      {/* Kept apart from everything above: signing out is not an edit, and a destructive
          control beside a Save button is one mis-tap from losing a form. */}
      <div className="mt-10 border-t pt-5" style={{ borderColor: 'var(--color-line)' }}>
        <Button variant="ghost" onClick={signOut}>
          {t('pr.signOut')}
        </Button>
      </div>
    </section>
  );
}

function Group({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <fieldset className="flex flex-col gap-3">
        <legend className="text-sm font-semibold">{title}</legend>
        {hint && (
          <p className="-mt-1 text-xs" style={{ color: 'var(--color-muted)' }}>
            {hint}
          </p>
        )}
        {children}
      </fieldset>
    </Card>
  );
}

/**
 * The picture, and the initials that stand in for it.
 *
 * No stock silhouette: a grey outline of a person says "we have nothing" in a way that
 * initials in the operator's own colour do not.
 */
function AvatarRow({
  name,
  url,
  onChange,
}: {
  name: string;
  url: string | null;
  onChange: (next: string | null) => void;
}) {
  const t = useT();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function upload(file: File) {
    setBusy(true);
    setError(null);
    try {
      const body = new FormData();
      body.append('file', file);
      // upload(), not request(): the browser must set the multipart boundary itself.
      const updated = await api.upload<AuthenticatedUser>('/public/auth/profile/avatar', body);
      onChange(updated.avatarUrl);
    } catch {
      setError(t('pr.photoFailed'));
    } finally {
      setBusy(false);
      // Cleared so choosing the SAME file again still fires a change event.
      if (input.current) input.current.value = '';
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await api.delete('/public/auth/profile/avatar');
      onChange(null);
    } catch {
      setError(t('pr.photoFailed'));
    } finally {
      setBusy(false);
    }
  }

  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');

  return (
    <div className="mt-5 flex items-center gap-4">
      {url ? (
        <img
          src={url}
          alt=""
          width={72}
          height={72}
          className="h-18 w-18 rounded-full object-cover"
          style={{ height: 72, width: 72 }}
        />
      ) : (
        <span
          aria-hidden
          className="grid shrink-0 place-items-center rounded-full text-xl font-semibold"
          style={{
            height: 72,
            width: 72,
            background: 'var(--color-primary-wash)',
            color: 'var(--color-primary)',
          }}
        >
          {initials || '·'}
        </span>
      )}

      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" loading={busy} onClick={() => input.current?.click()}>
            {url ? t('pr.changePhoto') : t('pr.addPhoto')}
          </Button>
          {url && (
            <Button size="sm" variant="ghost" onClick={remove}>
              {t('pr.removePhoto')}
            </Button>
          )}
        </div>
        {error && (
          <p className="text-xs" style={{ color: 'var(--signal-error, #b42318)' }}>
            {error}
          </p>
        )}
      </div>

      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void upload(file);
        }}
      />
    </div>
  );
}
